/**
 * Line chart (CLAUDE.md §6.10). Colours come only from the --chart-* variables, so both themes
 * work without chart code. Keyboard: focus the chart, then ←/→ (⇧ for 10 points), Home/End.
 */
import { curveLinear, curveStepAfter } from '@visx/curve';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { useId, useMemo, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useElementWidth } from './use-element-width';

export interface Point {
  x: Date;
  y: number;
}

export type SeriesColor = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface Series {
  id: string;
  label: string;
  color: SeriesColor;
  points: readonly Point[];
  step?: boolean;
  /** Single-series area fill at 12 % opacity. */
  area?: boolean;
}

export interface ReferenceLine {
  id: string;
  y: number;
  label: string;
  kind: 'level' | 'target';
}

export interface Marker {
  id: string;
  x: Date;
  y: number;
  label: string;
}

export interface LineChartProps {
  /** Accessible name; the chart card also offers a table. */
  label: string;
  series: readonly Series[];
  references?: readonly ReferenceLine[];
  markers?: readonly Marker[];
  height?: number;
  formatY: (value: number) => string;
  formatXTick: (value: Date) => string;
  formatXLong: (value: Date) => string;
}

const FONT = 11;
const MARGIN = { top: 8, right: 64, bottom: FONT + 14, left: 8 };
/** Roughly one x label per 90 px and one y label per 45 px. */
const X_TICK_SPACING = 90;
const Y_TICK_SPACING = 45;

function color(slot: SeriesColor): string {
  return `var(--chart-series-${String(slot)})`;
}

function nearestIndex(points: readonly Point[], time: number): number {
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if ((points[mid]?.x.getTime() ?? 0) < time) low = mid + 1;
    else high = mid;
  }
  const prev = points[low - 1];
  const here = points[low];
  if (prev && here && time - prev.x.getTime() < here.x.getTime() - time) return low - 1;
  return low;
}

export function LineChart({
  label,
  series,
  references = [],
  markers = [],
  height = 220,
  formatY,
  formatXTick,
  formatXLong,
}: LineChartProps) {
  const [ref, width] = useElementWidth();
  const [hover, setHover] = useState<number | null>(null);
  const tooltipId = useId();
  const primary = series[0]?.points ?? [];

  const { x, y, innerWidth, innerHeight } = useMemo(() => {
    const all = series.flatMap((s) => s.points);
    const xs = all.map((p) => p.x.getTime());
    const ys = [...all.map((p) => p.y), ...references.map((r) => r.y), ...markers.map((m) => m.y)];
    const minX = xs.length ? Math.min(...xs) : 0;
    const maxX = xs.length ? Math.max(...xs) : 1;
    let minY = ys.length ? Math.min(...ys) : 0;
    let maxY = ys.length ? Math.max(...ys) : 1;
    if (minY === maxY) {
      minY -= 1;
      maxY += 1;
    }
    const pad = (maxY - minY) * 0.06;
    const innerW = Math.max(0, width - MARGIN.left - MARGIN.right);
    const innerH = Math.max(0, height - MARGIN.top - MARGIN.bottom);
    return {
      x: scaleTime({ domain: [new Date(minX), new Date(maxX)], range: [0, innerW] }),
      y: scaleLinear({ domain: [minY - pad, maxY + pad], range: [innerH, 0], nice: true }),
      innerWidth: innerW,
      innerHeight: innerH,
    };
  }, [series, references, markers, width, height]);

  const hovered = hover !== null ? primary[hover] : undefined;
  const focusPoint = hovered ?? primary[primary.length - 1];
  const valueText = focusPoint ? `${formatXLong(focusPoint.x)}: ${formatY(focusPoint.y)}` : label;

  function onPointer(event: PointerEvent<SVGRectElement>): void {
    if (!primary.length) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const time = x.invert(event.clientX - bounds.left).getTime();
    setHover(nearestIndex(primary, time));
  }

  function onKey(event: KeyboardEvent<HTMLDivElement>): void {
    if (!primary.length) return;
    const last = primary.length - 1;
    const step = event.shiftKey ? 10 : 1;
    const current = hover ?? last;
    const next =
      event.key === 'ArrowLeft'
        ? Math.max(0, current - step)
        : event.key === 'ArrowRight'
          ? Math.min(last, current + step)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next !== null) {
      event.preventDefault();
      setHover(next);
    } else if (event.key === 'Escape') {
      setHover(null);
    }
  }

  const yTicks = y.ticks(Math.max(2, Math.floor(innerHeight / Y_TICK_SPACING)));
  const xTicks = x.ticks(Math.max(2, Math.floor(innerWidth / X_TICK_SPACING)));

  return (
    // Keyboard users step through the points like a slider over dates; the value text is what
    // the tooltip shows.
    <div
      ref={ref}
      className="relative rounded-md outline-none focus-visible:ring-2 focus-visible:ring-focus"
      role="slider"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.max(0, primary.length - 1)}
      aria-valuenow={hover ?? Math.max(0, primary.length - 1)}
      aria-valuetext={valueText}
      aria-describedby={hovered ? tooltipId : undefined}
      tabIndex={0}
      onKeyDown={onKey}
      onBlur={() => {
        setHover(null);
      }}
    >
      {width > 0 && (
        <svg width={width} height={height} aria-hidden>
          <Group left={MARGIN.left} top={MARGIN.top}>
            {yTicks.map((tick) => (
              <g key={`y${String(tick)}`}>
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={y(tick)}
                  y2={y(tick)}
                  strokeWidth={0.75}
                  style={{ stroke: 'var(--chart-grid)' }}
                />
                <text
                  x={innerWidth + 8}
                  y={y(tick)}
                  dy="0.32em"
                  className="tabular"
                  style={{ fill: 'var(--chart-axis-label)', fontSize: FONT }}
                >
                  {formatY(tick)}
                </text>
              </g>
            ))}
            <line
              x1={0}
              x2={innerWidth}
              y1={innerHeight}
              y2={innerHeight}
              strokeWidth={1}
              style={{ stroke: 'var(--chart-axis)' }}
            />
            {xTicks.map((tick) => (
              <text
                key={`x${String(tick.getTime())}`}
                x={x(tick)}
                y={innerHeight + FONT + 7}
                textAnchor="middle"
                className="tabular"
                style={{ fill: 'var(--chart-axis-label)', fontSize: FONT }}
              >
                {formatXTick(tick)}
              </text>
            ))}

            {references.map((line) => (
              <g key={line.id}>
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={y(line.y)}
                  y2={y(line.y)}
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  style={{
                    stroke:
                      line.kind === 'target' ? 'var(--text-positive)' : 'var(--chart-axis-label)',
                  }}
                />
                <text
                  x={innerWidth - 4}
                  y={y(line.y) - 4}
                  textAnchor="end"
                  className="tabular"
                  style={{
                    fill:
                      line.kind === 'target' ? 'var(--text-positive)' : 'var(--chart-axis-label)',
                    fontSize: FONT,
                  }}
                >
                  {line.label}
                </text>
              </g>
            ))}

            {series.map((s) => (
              <g key={s.id}>
                {s.area === true && (
                  <AreaClosed
                    data={[...s.points]}
                    x={(p) => x(p.x)}
                    y={(p) => y(p.y)}
                    yScale={y}
                    curve={s.step === true ? curveStepAfter : curveLinear}
                    style={{ fill: color(s.color), opacity: 0.12 }}
                  />
                )}
                <LinePath
                  data={[...s.points]}
                  x={(p) => x(p.x)}
                  y={(p) => y(p.y)}
                  curve={s.step === true ? curveStepAfter : curveLinear}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  style={{ stroke: color(s.color), fill: 'none' }}
                />
              </g>
            ))}

            {markers.map((m) => (
              <circle
                key={m.id}
                cx={x(m.x)}
                cy={y(m.y)}
                r={3}
                strokeWidth={1.5}
                style={{ fill: 'var(--text-1)', stroke: 'var(--chart-surface)' }}
              />
            ))}

            {hovered && (
              <g>
                <line
                  x1={x(hovered.x)}
                  x2={x(hovered.x)}
                  y1={0}
                  y2={innerHeight}
                  strokeWidth={1}
                  style={{ stroke: 'var(--chart-crosshair)' }}
                />
                {series.map((s) => {
                  const point = s.points[nearestIndex(s.points, hovered.x.getTime())];
                  return point ? (
                    <circle
                      key={s.id}
                      cx={x(point.x)}
                      cy={y(point.y)}
                      r={4}
                      strokeWidth={2}
                      style={{ fill: color(s.color), stroke: 'var(--chart-surface)' }}
                    />
                  ) : null;
                })}
              </g>
            )}

            <rect
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onPointerMove={onPointer}
              onPointerLeave={() => {
                setHover(null);
              }}
            />
          </Group>
        </svg>
      )}
      {hovered && (
        <div
          id={tooltipId}
          role="status"
          className="pointer-events-none absolute top-2 z-50 flex max-w-72 animate-fade-in flex-col gap-0.5 rounded-md bg-bg-2 px-2 py-1 text-xs text-text-1 shadow-popover"
          style={
            x(hovered.x) > innerWidth / 2
              ? { right: width - x(hovered.x) - MARGIN.left + 12 }
              : { left: x(hovered.x) + MARGIN.left + 12 }
          }
        >
          <span className="text-text-3">{formatXLong(hovered.x)}</span>
          {series.map((s) => {
            const point = s.points[nearestIndex(s.points, hovered.x.getTime())];
            return point ? (
              <span key={s.id} className="flex items-center justify-between gap-3">
                {series.length > 1 && <span className="text-text-2">{s.label}</span>}
                <span className="tabular font-medium">{formatY(point.y)}</span>
              </span>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}
